import React from "react";
import { Alert, Button, Col, Container, Row, Spinner, Table } from "react-bootstrap";
import { Link, Navigate, useLocation } from "react-router-dom";
import { Shoptopbar } from "Components/ShopTopBar";
import { CommonService } from "Components/CommonService";
import { useGetStoreMeQuery } from "../../redux/services/store/storeAuthApi";
import { useGetStoreOrdersQuery } from "../../redux/services/store/storeCheckoutApi";

const canShowInvoice = (paymentStatus?: string | null) => {
  const normalized = String(paymentStatus || "").toLowerCase();
  return normalized === "paid" || normalized === "captured";
};

const getCustomizationSummary = (item?: {
  customization_enabled?: boolean | null;
  customization_color?: string | null;
  customization_note?: string | null;
}) => {
  const color = String(item?.customization_color || "").trim();
  const note = String(item?.customization_note || "").trim();
  const enabled = Boolean(item?.customization_enabled || color || note);
  if (!enabled) return "";

  const parts = [];
  if (color) parts.push(`Custom color: ${color}`);
  if (note) parts.push(`Custom note: ${note}`);
  return parts.length ? parts.join(" | ") : "Customization requested";
};

const orderHistorySkeletonBaseStyle: React.CSSProperties = {
  background: "linear-gradient(90deg, #f1e7df 25%, #fbf7f3 50%, #f1e7df 75%)",
  backgroundSize: "200% 100%",
  animation: "orderHistorySkeletonShimmer 1.4s ease infinite",
  borderRadius: 14,
};

const OrderHistorySkeleton = () => (
  <>
    <style>
      {`
        @keyframes orderHistorySkeletonShimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}
    </style>
    <Shoptopbar title="Order History" page="Order History" />
    <section className="section">
      <Container>
        <Row>
          <Col lg={12}>
            <div style={{ ...orderHistorySkeletonBaseStyle, height: 22, width: 220, marginBottom: 20 }} />
            <div className="table-responsive">
              <Table className="fs-15 align-middle table-nowrap">
                <thead>
                  <tr>
                    <th scope="col">Order ID</th>
                    <th scope="col">Product</th>
                    <th scope="col">Date</th>
                    <th scope="col">Total Amount</th>
                    <th scope="col">Status</th>
                    <th scope="col"></th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 5 }).map((_, index) => (
                    <tr key={`order-history-skeleton-${index}`}>
                      <td>
                        <div style={{ ...orderHistorySkeletonBaseStyle, height: 16, width: 90 }} />
                      </td>
                      <td>
                        <div style={{ ...orderHistorySkeletonBaseStyle, height: 16, width: "55%", marginBottom: 10 }} />
                        <div style={{ ...orderHistorySkeletonBaseStyle, height: 13, width: "35%" }} />
                      </td>
                      <td>
                        <div style={{ ...orderHistorySkeletonBaseStyle, height: 16, width: 110 }} />
                      </td>
                      <td>
                        <div style={{ ...orderHistorySkeletonBaseStyle, height: 16, width: 80 }} />
                      </td>
                      <td>
                        <div style={{ ...orderHistorySkeletonBaseStyle, height: 28, width: 92, borderRadius: 999 }} />
                      </td>
                      <td>
                        <div style={{ ...orderHistorySkeletonBaseStyle, height: 32, width: 76 }} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
    <CommonService />
  </>
);

const Orderhistory = () => {
  document.title = "Order History | Manser Commerce";

  const location = useLocation();

  const {
    data: me,
    isLoading: isMeLoading,
    isFetching: isMeFetching,
    isSuccess: isMeSuccess,
  } = useGetStoreMeQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const isCheckingAuth = isMeLoading || isMeFetching;
  const isLoggedIn = isMeSuccess && !!me?.id;

  const {
    data: ordersData,
    isLoading: isOrdersLoading,
    isFetching: isOrdersFetching,
    isError: isOrdersError,
    refetch: refetchOrders,
  } = useGetStoreOrdersQuery(
    { skip: 0, limit: 50 },
    {
      skip: !isLoggedIn,
      refetchOnMountOrArgChange: true,
    }
  );

  const formatMoney = (value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === "") return "0";
    return `${value}`;
  };

  const formatDate = (value?: string | null) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString();
  };

  const getStatusVariant = (status?: string | null) => {
    const value = String(status || "").toLowerCase();
    if (["delivered", "completed", "paid"].includes(value)) return "success";
    if (["pending", "processing", "placed"].includes(value)) return "warning";
    if (["cancelled", "failed", "returned"].includes(value)) return "danger";
    return "info";
  };

  if (isCheckingAuth) {
    return <OrderHistorySkeleton />;
  }

  if (!isLoggedIn) {
    return (
      <Navigate
        to="/auth-signin-basic"
        replace
        state={{
          from: {
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
          },
        }}
      />
    );
  }

  return (
    <React.Fragment>
      <Shoptopbar title="Order History" page="Order History" />
      <section className="section">
        <Container>
          <Row>
            <Col lg={12}>
              <div>
                {isOrdersError ? (
                  <Alert variant="danger" className="d-flex justify-content-between align-items-center">
                    <span>Failed to load your orders.</span>
                    <Button variant="outline-danger" size="sm" onClick={() => refetchOrders()}>
                      Retry
                    </Button>
                  </Alert>
                ) : null}

                <div className="table-responsive">
                  <Table className="fs-15 align-middle table-nowrap">
                    <thead>
                      <tr>
                        <th scope="col">Order ID</th>
                        <th scope="col">Product</th>
                        <th scope="col">Date</th>
                        <th scope="col">Total Amount</th>
                        <th scope="col">Status</th>
                        <th scope="col"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {isOrdersLoading || isOrdersFetching ? (
                        <tr>
                          <td colSpan={6} className="py-2">
                            {Array.from({ length: 4 }).map((_, index) => (
                              <div
                                key={`order-inline-skeleton-${index}`}
                                style={{
                                  ...orderHistorySkeletonBaseStyle,
                                  height: 44,
                                  marginBottom: index === 3 ? 0 : 12,
                                }}
                              />
                            ))}
                          </td>
                        </tr>
                      ) : (ordersData?.items || []).length > 0 ? (
                        (ordersData?.items || []).map((item) => {
                          const firstItem = item.items?.[0];
                          const statusVariant = getStatusVariant(item.status);
                          return (
                            <tr key={item.id}>
                              <td>
                                <Link to="#" className="text-body">
                                  {item.order_no}
                                </Link>
                              </td>
                              <td>
                                <div className="flex-grow-1">
                                  <Link
                                    to={
                                      firstItem?.product_id
                                        ? `/product/${firstItem.product_id}`
                                        : "/product-list"
                                    }
                                  >
                                    <h6 className="fs-15 mb-1">
                                      {firstItem?.product_name_snapshot || "Order Item"}
                                    </h6>
                                  </Link>
                                  <p className="mb-0 text-muted fs-13">
                                    {[
                                      firstItem?.variant_name_snapshot || "",
                                      getCustomizationSummary(firstItem),
                                    ]
                                      .filter(Boolean)
                                      .join(" | ") || `${item.items?.length || 0} item(s)`}
                                  </p>
                                </div>
                              </td>
                              <td>
                                <span className="text-muted">{formatDate(item.placed_at)}</span>
                              </td>
                              <td className="fw-medium">₹{formatMoney(item.grand_total)}</td>
                              <td>
                                <span className={`badge bg-${statusVariant}-subtle text-${statusVariant}`}>
                                  {item.status}
                                </span>
                              </td>
                              <td>
                                {canShowInvoice((item as any)?.payment_status) ? (
                                  <Link to={`/invoice/${item.id}`} className="btn btn-secondary btn-sm">
                                    Invoice
                                  </Link>
                                ) : (
                                  <span className="text-muted fs-12">Invoice after payment</span>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={6} className="text-center text-muted py-4">
                            No orders found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
                <div className="text-end">
                  <Link to="/" className="btn btn-primary btn-hover">
                    Continue Shopping <i className="ri-arrow-right-line align-middle ms-1"></i>
                  </Link>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
      <CommonService />
    </React.Fragment>
  );
};

export default Orderhistory;
